import React from 'react';
import Pagination from '../Common/Pagination';

const EndpointList = ({ endpoints, currentPage, totalPages, onPageChange, onEndpointClick }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(2) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(2) + 'K';
    }
    return num.toString();
  };

  const getMethodColor = (method) => {
    const colors = {
      GET: 'var(--success)',
      POST: 'var(--text-primary)',
      PUT: 'var(--warning)',
      PATCH: 'var(--text-secondary)',
      DELETE: 'var(--error)',
    };
    return colors[method] || 'var(--text-secondary)';
  };

  const getSuccessRateColor = (rate) => {
    if (rate >= 95) return 'var(--success)';
    if (rate >= 80) return 'var(--warning)';
    return 'var(--error)';
  };

  if (endpoints.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">🎯</div>
        <h3>No endpoints tested yet</h3>
        <p>Run your first test to see endpoints here</p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .endpoint-table-container {
          overflow-x: auto;
          margin: 20px 0;
          -webkit-overflow-scrolling: touch;
        }
        
        .endpoint-table {
          width: 100%;
          border-collapse: collapse;
          background: var(--card-bg);
          border-radius: 8px;
          overflow: hidden;
          border: 1px solid var(--border-color);
          min-width: 700px;
        }
        
        .endpoint-url-cell {
          font-family: monospace;
          font-size: 14px;
          max-width: 300px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        /* Mobile card view */
        .endpoint-cards {
          display: none;
        }
        
        .endpoint-card {
          background: var(--card-bg);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .endpoint-card:hover {
          border-color: var(--primary-black);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
        }
        
        .endpoint-card-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
        }
        
        .endpoint-card-url {
          font-family: monospace;
          font-size: 13px;
          color: var(--text-secondary);
          word-break: break-all;
          margin-bottom: 12px;
        }
        
        .endpoint-card-stats {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 8px;
        }
        
        .endpoint-card-stat {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        
        .endpoint-card-stat-label {
          font-size: 11px;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .endpoint-card-stat-value {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
        }
        
        @media (max-width: 768px) {
          .endpoint-table-container {
            display: none;
          }
          
          .endpoint-cards {
            display: block;
          }
        }
      `}</style>

      {/* Desktop Table View */}
      <div className="endpoint-table-container">
        <table className="endpoint-table">
          <thead>
            <tr>
              <th>Method</th>
              <th>URL</th>
              <th>Tests</th>
              <th>Requests</th>
              <th>Avg Response</th>
              <th>Success Rate</th>
              <th>Last Tested</th>
            </tr>
          </thead>
          <tbody>
            {endpoints.map((endpoint) => {
              const stats = endpoint.stats || {};
              const successRate = stats.successRate || 0;

              return (
                <tr key={endpoint._id} onClick={() => onEndpointClick(endpoint._id)}>
                  <td>
                    <span
                      className="badge"
                      style={{
                        background: `${getMethodColor(endpoint.method)}20`,
                        color: getMethodColor(endpoint.method),
                      }}
                    >
                      {endpoint.method}
                    </span>
                  </td>
                  <td className="endpoint-url-cell">
                    {endpoint.url}
                  </td>
                  <td>{endpoint.totalTests}</td>
                  <td>{formatNumber(stats.totalRequests || 0)}</td>
                  <td>
                    <span style={{ fontWeight: '500' }}>
                      {stats.avgResponseTime || 0}ms
                    </span>
                  </td>
                  <td>
                    <span
                      className="badge"
                      style={{
                        background: `${getSuccessRateColor(successRate)}15`,
                        color: getSuccessRateColor(successRate),
                        fontWeight: '600'
                      }}
                    >
                      {successRate}%
                    </span>
                  </td>
                  <td style={{ color: 'var(--text-secondary)' }}>
                    {formatDate(endpoint.lastTestedAt)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="endpoint-cards">
        {endpoints.map((endpoint) => {
          const stats = endpoint.stats || {};
          const successRate = stats.successRate || 0;

          return (
            <div
              key={endpoint._id}
              className="endpoint-card"
              onClick={() => onEndpointClick(endpoint._id)}
            >
              <div className="endpoint-card-header">
                <span
                  className="badge"
                  style={{
                    background: `${getMethodColor(endpoint.method)}20`,
                    color: getMethodColor(endpoint.method),
                  }}
                >
                  {endpoint.method}
                </span>
                <span
                  className="badge"
                  style={{
                    background: `${getSuccessRateColor(successRate)}15`,
                    color: getSuccessRateColor(successRate),
                    fontWeight: '600'
                  }}
                >
                  {successRate}%
                </span>
              </div>
              <div className="endpoint-card-url">{endpoint.url}</div>
              <div className="endpoint-card-stats">
                <div className="endpoint-card-stat">
                  <span className="endpoint-card-stat-label">Tests</span>
                  <span className="endpoint-card-stat-value">{endpoint.totalTests}</span>
                </div>
                <div className="endpoint-card-stat">
                  <span className="endpoint-card-stat-label">Requests</span>
                  <span className="endpoint-card-stat-value">{formatNumber(stats.totalRequests || 0)}</span>
                </div>
                <div className="endpoint-card-stat">
                  <span className="endpoint-card-stat-label">Avg Response</span>
                  <span className="endpoint-card-stat-value">{stats.avgResponseTime || 0}ms</span>
                </div>
                <div className="endpoint-card-stat">
                  <span className="endpoint-card-stat-label">Last Tested</span>
                  <span className="endpoint-card-stat-value" style={{ fontSize: '12px' }}>
                    {new Date(endpoint.lastTestedAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}
    </>
  );
};

export default EndpointList;
